import type { Column, Row } from "@tanstack/react-table"
import { formatNumber, formatPercent } from "@/v2-common/utils/utils.formatting"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import type { ColumnMeta, SummaryFraction } from "../table.types"
import {
  SUMMARY_AGGREGATION_LABELS,
  SUMMARY_BREAKDOWN_SEPARATOR,
  SUMMARY_DATE_AGGREGATION_LABELS,
  SUMMARY_EMPTY_VALUE,
  SUMMARY_STACK_MAX_SEGMENTS,
  SUMMARY_VALUES_SEPARATOR,
} from "./table.summary.constants"
import type {
  SummaryAggregationType,
  SummaryArithmeticType,
  SummaryBreakdownEntry,
  SummaryResult,
  SummaryValueKind,
  SummaryVisual,
} from "./table.summary.types"

/** ISO-8601 date or date-time: the shape GraphQL hands the table for dates. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:T|$)/
/** Decimals kept on a computed or listed number; whole numbers stay whole. */
const DECIMALS = 2
/** Decimals kept on a share. */
const SHARE_DECIMALS = 1
/** Joins a fraction's two parts the way the cells that show one do. */
const FRACTION_SEPARATOR = "/"

/**
 * One row's contribution to a column summary.
 *
 * `value` is what calculations run on; `display` is how it reads to the
 * operator, which the list-shaped summaries group by so a breakdown says
 * "12× Active" rather than "12× 0". A column's `summaryValue` sets both at
 * once. Otherwise `value` is the accessor value — raw numbers, ISO dates,
 * booleans — or, for a purely rendered column with no accessor value at all
 * (a progress bar built from several fields), its export value; and
 * `display` is the export value when the column defines one (status names
 * for status codes, Yes/No for flags), else the value itself.
 *
 * One exception: a number the column exports as a label — a status code
 * whose export is "Active" — is a code, not a quantity. Summing codes says
 * nothing, so such a row contributes the label on both counts, and the
 * column reads as text.
 */
export type SummaryContribution = {
  value: unknown
  display: unknown
}

/** A non-empty string that isn't a number written out. */
function isLabel(value: unknown): value is string {
  return (
    typeof value === "string" && value !== "" && Number.isNaN(Number(value))
  )
}

type SummaryEntry = {
  count: number
  /** A raw item behind the entry's key, so numbers and dates sort as such. */
  sample: unknown
}

export function collectSummaryValues<TData>(
  rows: Row<TData>[],
  column: Column<TData, unknown>,
): SummaryContribution[] {
  const meta = column.columnDef.meta as ColumnMeta<TData> | undefined
  const summaryValue = meta?.summaryValue
  const exportValue = meta?.exportValue
  const key = column.id

  return rows.map((row) => {
    const value = row.getValue(key)
    if (summaryValue) {
      const summarised = summaryValue({ key, column, row, value })
      return { value: summarised, display: summarised }
    }
    if (!exportValue) return { value, display: value }
    const exported = exportValue({ key, column, row, value })
    if (value === undefined) return { value: exported, display: exported }
    if (typeof value === "number" && isLabel(exported)) {
      return { value: exported, display: exported }
    }
    return { value, display: exported }
  })
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

/**
 * The summary value of a cell showing two numbers as one, "17/78": both
 * parts are calculated on, each on its own. A missing part, or nothing under
 * the line, is no quantity at all, so the row counts as empty.
 */
export function summaryFraction(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): SummaryFraction | null {
  if (!isFiniteNumber(numerator) || !isFiniteNumber(denominator)) return null
  if (denominator === 0) return null
  return { numerator, denominator }
}

function isSummaryFraction(value: unknown): value is SummaryFraction {
  if (typeof value !== "object" || value === null) return false
  const { numerator, denominator } = value as Partial<SummaryFraction>
  return isFiniteNumber(numerator) && isFiniteNumber(denominator)
}

function isEmptySummaryValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true
  return Array.isArray(value) && value.length === 0
}

function isDateValue(value: unknown): value is Date | string {
  if (value instanceof Date) return true
  return typeof value === "string" && ISO_DATE_PATTERN.test(value)
}

/**
 * Decides a column's value kind from its non-empty values: every one a
 * number → number, every one a fraction → fraction, every one a date → date,
 * anything else → text. Stops at the first value that rules them all out.
 */
export function detectSummaryValueKind(
  values: SummaryContribution[],
): SummaryValueKind {
  let hasValue = false
  let allNumbers = true
  let allFractions = true
  let allDates = true

  for (const { value } of values) {
    if (isEmptySummaryValue(value)) continue
    hasValue = true
    if (typeof value !== "number") allNumbers = false
    if (!isSummaryFraction(value)) allFractions = false
    if (!isDateValue(value)) allDates = false
    if (!allNumbers && !allFractions && !allDates) return "text"
  }

  if (!hasValue) return "text"
  if (allNumbers) return "number"
  if (allFractions) return "fraction"
  return "date"
}

export function isSummaryAggregationType(
  value: unknown,
): value is SummaryAggregationType {
  return typeof value === "string" && value in SUMMARY_AGGREGATION_LABELS
}

function getSummaryAggregationLabel(
  type: SummaryAggregationType,
  kind: SummaryValueKind,
): string {
  if (kind === "date") {
    return (
      SUMMARY_DATE_AGGREGATION_LABELS[type] ?? SUMMARY_AGGREGATION_LABELS[type]
    )
  }
  return SUMMARY_AGGREGATION_LABELS[type]
}

function formatFraction(numerator: number, denominator: number): string {
  return `${formatNumber(numerator, DECIMALS)}${FRACTION_SEPARATOR}${formatNumber(denominator, DECIMALS)}`
}

/** Renders one item the way the operator would read it in a cell. */
function formatSummaryItem(item: unknown): string {
  if (typeof item === "boolean") return item ? "Yes" : "No"
  if (typeof item === "number") return formatNumber(item, DECIMALS)
  if (isSummaryFraction(item)) {
    return formatFraction(item.numerator, item.denominator)
  }
  if (isDateValue(item)) return formatDateString(item)
  return String(item)
}

function collectNumbers(values: SummaryContribution[]): number[] {
  const numbers: number[] = []
  for (const { value } of values) {
    if (isFiniteNumber(value)) numbers.push(value)
  }
  return numbers
}

function collectFractions(values: SummaryContribution[]): SummaryFraction[] {
  const fractions: SummaryFraction[] = []
  for (const { value } of values) {
    if (isSummaryFraction(value)) fractions.push(value)
  }
  return fractions
}

function toTime(value: Date | string): number {
  return value instanceof Date ? value.getTime() : Date.parse(value)
}

function fractionRatio(fraction: SummaryFraction): number {
  return fraction.numerator / fraction.denominator
}

/**
 * Distinct display items with how often each occurs. A row holding an array
 * (tags, countries) contributes every item, so the list is of the items
 * themselves rather than of each row's joined string. Dates collapse to the
 * day they render as.
 */
function countEntries(
  values: SummaryContribution[],
): Map<string, SummaryEntry> {
  const entries = new Map<string, SummaryEntry>()

  function add(key: string, sample: unknown) {
    const entry = entries.get(key)
    if (entry) entry.count += 1
    else entries.set(key, { count: 1, sample })
  }

  for (const { value, display } of values) {
    if (isEmptySummaryValue(value)) continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (!isEmptySummaryValue(item)) add(formatSummaryItem(item), item)
      }
    } else if (!isEmptySummaryValue(display)) {
      add(formatSummaryItem(display), value)
    }
  }

  return entries
}

type EntryPair = [key: string, entry: SummaryEntry]

/**
 * Reading order for entries of the column's kind: numbers ascending,
 * fractions by the share they make, dates chronologically, text
 * alphabetically.
 */
function compareEntries(
  kind: SummaryValueKind,
): (a: EntryPair, b: EntryPair) => number {
  if (kind === "number") {
    return (a, b) => (a[1].sample as number) - (b[1].sample as number)
  }
  if (kind === "fraction") {
    return (a, b) => {
      const left = a[1].sample as SummaryFraction
      const right = b[1].sample as SummaryFraction
      return (
        fractionRatio(left) - fractionRatio(right) ||
        left.denominator - right.denominator
      )
    }
  }
  if (kind === "date") {
    return (a, b) =>
      toTime(a[1].sample as Date | string) -
      toTime(b[1].sample as Date | string)
  }
  return (a, b) =>
    a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: "base" })
}

function sumOf(numbers: number[]): number {
  return numbers.reduce((total, number) => total + number, 0)
}

function medianOf(numbers: number[]): number {
  const sorted = [...numbers].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle]
}

/** Loops rather than spreads: a board can hold more rows than a call takes arguments. */
function extremeOf(numbers: number[], type: "min" | "max"): number {
  return numbers.reduce((best, number) =>
    type === "min" ? Math.min(best, number) : Math.max(best, number),
  )
}

/** One number calculation over plain numbers; null when there are none. */
function computeArithmetic(
  numbers: number[],
  type: SummaryArithmeticType,
): number | null {
  if (numbers.length === 0) return null
  switch (type) {
    case "sum":
      return sumOf(numbers)
    case "average":
      return sumOf(numbers) / numbers.length
    case "median":
      return medianOf(numbers)
    case "min":
    case "max":
      return extremeOf(numbers, type)
  }
}

function computeExtremeDate(
  values: SummaryContribution[],
  type: "min" | "max",
): string {
  let bestTime: number | null = null
  let bestValue: Date | string | null = null

  for (const { value } of values) {
    if (!isDateValue(value)) continue
    const time = toTime(value)
    if (Number.isNaN(time)) continue
    const isBetter =
      bestTime === null || (type === "min" ? time < bestTime : time > bestTime)
    if (isBetter) {
      bestTime = time
      bestValue = value
    }
  }

  return bestValue === null ? SUMMARY_EMPTY_VALUE : formatDateString(bestValue)
}

/** A calculation's outcome before it is labelled. */
type SummaryAnswer = Omit<SummaryResult, "label">

function answer(
  value: string,
  visual: SummaryVisual | null = null,
): SummaryAnswer {
  return { value, visual }
}

const EMPTY_ANSWER = answer(SUMMARY_EMPTY_VALUE)

/**
 * A number calculation over plain numbers. An average or median also says
 * where it falls between the column's min and max — unless every value is
 * the same, when there is no between.
 */
function computeNumberSummary(
  numbers: number[],
  type: SummaryArithmeticType,
): SummaryAnswer {
  const result = computeArithmetic(numbers, type)
  if (result === null) return EMPTY_ANSWER
  const value = formatNumber(result, DECIMALS)
  if (type !== "average" && type !== "median") return answer(value)

  const min = extremeOf(numbers, "min")
  const max = extremeOf(numbers, "max")
  if (max === min) return answer(value)
  return answer(value, {
    type: "position",
    share: (result - min) / (max - min),
    min: formatNumber(min, DECIMALS),
    max: formatNumber(max, DECIMALS),
  })
}

/**
 * A number calculation on each part of the fractions on its own, so an
 * average of "1/78, 2/2" reads "1,5/40".
 */
function computeFractionSummary(
  fractions: SummaryFraction[],
  type: SummaryArithmeticType,
): SummaryAnswer {
  const numerator = computeArithmetic(
    fractions.map((fraction) => fraction.numerator),
    type,
  )
  const denominator = computeArithmetic(
    fractions.map((fraction) => fraction.denominator),
    type,
  )
  if (numerator === null || denominator === null) return EMPTY_ANSWER
  return answer(formatFraction(numerator, denominator))
}

/**
 * A number calculation for the column's kind: over the numbers themselves,
 * over each part of the fractions, or — for the extremes only — over the
 * dates.
 */
function computeArithmeticSummary(
  values: SummaryContribution[],
  type: SummaryArithmeticType,
  kind: SummaryValueKind,
): SummaryAnswer {
  if (kind === "number")
    return computeNumberSummary(collectNumbers(values), type)
  if (kind === "fraction") {
    return computeFractionSummary(collectFractions(values), type)
  }
  if (kind === "date" && (type === "min" || type === "max")) {
    return answer(computeExtremeDate(values, type))
  }
  return EMPTY_ANSWER
}

/** The share a fraction column's summed numerators make of its summed denominators. */
function computeShare(
  values: SummaryContribution[],
  kind: SummaryValueKind,
): SummaryAnswer {
  if (kind !== "fraction") return EMPTY_ANSWER
  const fractions = collectFractions(values)
  const denominator = sumOf(fractions.map((fraction) => fraction.denominator))
  if (denominator === 0) return EMPTY_ANSWER
  const share =
    sumOf(fractions.map((fraction) => fraction.numerator)) / denominator
  return answer(formatPercent(share * 100, SHARE_DECIMALS), {
    type: "meter",
    share,
  })
}

/** How many rows are filled or empty, and the share of all rows they make. */
function computeCount(
  values: SummaryContribution[],
  type: "filled" | "empty",
): SummaryAnswer {
  const empty = values.filter((v) => isEmptySummaryValue(v.value)).length
  const count = type === "empty" ? empty : values.length - empty
  if (values.length === 0) return answer(formatNumber(count))
  return answer(formatNumber(count), {
    type: "meter",
    share: count / values.length,
  })
}

/**
 * The distinct values with how often each occurs, most common first — ties
 * in the kind's reading order — and each one's share of the counted rows.
 */
function breakdownEntries(
  values: SummaryContribution[],
  kind: SummaryValueKind,
): SummaryBreakdownEntry[] {
  const counted = [...countEntries(values)]
  const compare = compareEntries(kind)
  const total = counted.reduce((sum, [, entry]) => sum + entry.count, 0)
  return counted
    .sort((a, b) => b[1].count - a[1].count || compare(a, b))
    .map(([key, entry]) => ({
      key,
      count: entry.count,
      share: entry.count / total,
    }))
}

/** One breakdown entry the way it reads in a cell: "12× Active". */
export function formatBreakdownEntry(entry: SummaryBreakdownEntry): string {
  return `${formatNumber(entry.count)}× ${entry.key}`
}

/** A breakdown entry's share, whole percent, as its legend shows it. */
export function formatBreakdownShare(entry: SummaryBreakdownEntry): string {
  return formatPercent(entry.share * 100, 0)
}

/**
 * The colour a breakdown entry keeps everywhere it appears — its segment of
 * the stacked bar, its dot in the text, its row in the legend — by its rank.
 * The chart palette covers the most common entries; every one past it shares
 * the neutral, so a long tail reads as "the rest" rather than as noise.
 */
export function getBreakdownEntryColor(index: number): string {
  return index < SUMMARY_STACK_MAX_SEGMENTS
    ? DATA_VIZ.series[index]
    : DATA_VIZ.semantic.neutral
}

function computeSummaryAnswer(
  values: SummaryContribution[],
  type: SummaryAggregationType,
  kind: SummaryValueKind,
): SummaryAnswer {
  switch (type) {
    case "sum":
    case "average":
    case "median":
    case "min":
    case "max":
      return computeArithmeticSummary(values, type, kind)
    case "share":
      return computeShare(values, kind)
    case "filled":
    case "empty":
      return computeCount(values, type)
    case "unique":
      return answer(formatNumber(countEntries(values).size))
    case "values": {
      const entries = [...countEntries(values)]
      if (entries.length === 0) return EMPTY_ANSWER
      return answer(
        entries
          .sort(compareEntries(kind))
          .map(([key]) => key)
          .join(SUMMARY_VALUES_SEPARATOR),
      )
    }
    case "breakdown": {
      const entries = breakdownEntries(values, kind)
      if (entries.length === 0) return EMPTY_ANSWER
      return answer(
        entries.map(formatBreakdownEntry).join(SUMMARY_BREAKDOWN_SEPARATOR),
        { type: "stack", entries },
      )
    }
  }
}

/**
 * Runs one calculation over a column's values. Tolerates a calculation the
 * values no longer suit (a remembered "sum" on a column that is text after a
 * filter change) by reporting the empty value rather than failing.
 */
export function computeSummary(input: {
  values: SummaryContribution[]
  type: SummaryAggregationType
  kind: SummaryValueKind
}): SummaryResult {
  const { values, type, kind } = input
  return {
    label: getSummaryAggregationLabel(type, kind),
    ...computeSummaryAnswer(values, type, kind),
  }
}
